import type { IPublicClientApplication } from "@azure/msal-browser";
import type { OneDriveDrive, OneDriveDriveItem, OneDriveFolderNode } from "./types/oneDrive";

let ableToAccessOneDrive: boolean = false;
let accessToken: string | null = null;
let tokenExpiry: number;
let msalInstance: IPublicClientApplication | undefined = undefined;
// Initialize OneDrive access on module load
let initializePromise: Promise<void> | null = null;

export function isOneDriveAccessInitialized(): boolean {
    return initializePromise !== null && ableToAccessOneDrive;
}

export async function initializeOneDriveAccess(): Promise<boolean> {
    if (!msalInstance) {
        await getMSAL();
        if (!msalInstance) {
            ableToAccessOneDrive = false;
            console.error("MSAL instance is not initialized.");
            return false;
        }
    }
    initializePromise = getToken();
    await initializePromise;
    return ableToAccessOneDrive;
}

async function getMSAL(): Promise<void> {
    const naaSupported = Office.context.requirements.isSetSupported("NestedAppAuth", "1.1");
    if (naaSupported) {
        if (!msalInstance) {
            const clientId = (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') ? "503db1f9-0a89-42c0-8e14-bd42627693fd" : "e5fa610d-1ad2-4250-8af7-6aff9c5894ed";
            const msalConfig = {
                auth: {
                    clientId: clientId,
                    authority: 'https://login.microsoftonline.com/common',
                },
                cache: {
                    cacheLocation: "localStorage"
                }
            };
            const createNestablePublicClientApplication = (await import("@azure/msal-browser")).createNestablePublicClientApplication;
            msalInstance = await createNestablePublicClientApplication(msalConfig);
        }
    }
}

async function getToken() {
    if (!msalInstance) {
        await getMSAL();
        if (!msalInstance) {
            ableToAccessOneDrive = false;
            console.error("MSAL instance is not initialized.");
            return;
        }
    }

    const tokenRequest = {
        scopes: ["User.Read", "Files.ReadWrite"],
    }
    try {
        const userAccount = await msalInstance.acquireTokenSilent(tokenRequest);
        accessToken = userAccount.accessToken;
        tokenExpiry = (userAccount.expiresOn?.getTime() || (Date.now() + 3600000)) - 60000; // Set expiry to 1 minute before actual expiry to ensure we refresh in time
        ableToAccessOneDrive = true;
    } catch (error) {
        const { InteractionRequiredAuthError } = await import("@azure/msal-browser");
        if (error instanceof InteractionRequiredAuthError) {
            try {
                console.warn("Silent token acquisition failed, attempting popup.");
                const userAccount = await msalInstance.acquireTokenPopup(tokenRequest);
                accessToken = userAccount.accessToken;
                tokenExpiry = (userAccount.expiresOn?.getTime() || (Date.now() + 3600000)) - 60000; // Set expiry to 1 minute before actual expiry to ensure we refresh in time
                ableToAccessOneDrive = true;
            } catch (popupError) {
                console.error("Popup token acquisition failed:", popupError);
                ableToAccessOneDrive = false;
            }
        } else {
            console.error("Token acquisition failed");
            ableToAccessOneDrive = false;
        }
    }
}

async function graphApiCall<T>(endpoint: string, options: RequestInit = {}, responseType: 'json' | 'blob' = 'json', token?: string): Promise<T> {

    for (let tries = 0; tries < 3; tries++) {
        const useToken = token || accessToken;
        if (!useToken || (tokenExpiry && Date.now() >= tokenExpiry)) {
            await getToken();
        }
        if (!ableToAccessOneDrive && !token) {
            throw new Error("Unable to access OneDrive due to authentication failure.");
        }
        try {
            const defaultOptions: RequestInit = {
                headers: {
                    Authorization: `Bearer ${useToken || accessToken}`,
                    "Content-Type": "application/json",
                    ...options.headers,
                },
            };
            const response = await fetch(`https://graph.microsoft.com/v1.0${endpoint}`, {
                ...defaultOptions,
                ...options,
            });
            if (!response.ok) {
                if (response.status === 401) {
                    ableToAccessOneDrive = false;
                    accessToken = null;
                    tokenExpiry = 0;
                }
                throw new Error(`Graph API call failed: ${response.status} ${response.statusText}`);
            }

            if (responseType === 'blob') {
                return await response.blob() as unknown as T;
            }

            return await response.json() as T;

        } catch (error) {
            if (tries === 2) {
                throw error;
            }
        }
        await new Promise(res => setTimeout(res, 1000 * tries)); // Exponential backoff
    }
    throw new Error("Graph API call failed after multiple attempts");
}

export async function getOneDriveRootFolder(): Promise<OneDriveDrive> {
    try {
        const drive = await graphApiCall<OneDriveDrive>(`/me/drive`);
        return drive;
    } catch (error) {
        console.error("Failed to fetch OneDrive root folder:", error);
        throw error;
    }
}

export async function getOneDriveFolders(folderId?: string): Promise<OneDriveDriveItem[]> {
    try {
        const endpoint = folderId ? `/me/drive/items/${folderId}/children` : '/me/drive/root/children';
        const response = await graphApiCall<{ value: OneDriveDriveItem[] }>(`${endpoint}?$filter=folder ne null&$orderby=name`);
        return response.value || [];
    } catch (error) {
        console.error("Failed to fetch OneDrive folders:", error);
        throw error;
    }
}

export async function createOneDriveFolder(parentId: string, folderName: string): Promise<OneDriveDriveItem> {
    try {
        const requestBody = {
            name: folderName,
            folder: {},
            "@microsoft.graph.conflictBehavior": "rename"
        };

        const endpoint = parentId === 'root' ? '/me/drive/root/children' : `/me/drive/items/${parentId}/children`;
        const newFolder = await graphApiCall<OneDriveDriveItem>(endpoint, {
            method: 'POST',
            body: JSON.stringify(requestBody)
        });

        return newFolder;
    } catch (error) {
        console.error("Failed to create OneDrive folder:", error);
        throw error;
    }
}

export function convertToFolderNodes(items: OneDriveDriveItem[], rootPath = ''): OneDriveFolderNode[] {
    return items.map(item => ({
        id: item.id,
        name: item.name,
        path: rootPath ? `${rootPath}/${item.name}` : item.name,
        children: [] // We'll load children on demand
    }));
}

export async function getFolderURL(folderId: string): Promise<string> {
    try {
        const endpoint = '/me/drive/items/' + folderId;
        const item = await graphApiCall<OneDriveDriveItem>(endpoint);
        return item.webUrl;
    } catch (error) {
        console.error("Failed to get folder URL:", error);
        return "";
    }
}

export async function setEmailCategory(emailId: string, categoryName: string): Promise<boolean> {
    try {

        const requiredCategories = [
            { displayName: "E-File: Queued", color: Office.MailboxEnums.CategoryColor.Preset12 },
            { displayName: "E-File: Archived", color: Office.MailboxEnums.CategoryColor.Preset6 },
            { displayName: "E-File: Error Archiving", color: Office.MailboxEnums.CategoryColor.Preset2 },
        ]

        const email = await graphApiCall<{ categories: string[] }>(`/me/messages/${emailId.replace(/[\\/]/g, "-")}?$select=categories`);
        const existingCategories = email.categories || [];

        const updatedCategories = existingCategories.filter(cat => !cat.startsWith("E-File:"));

        if (categoryName !== "") {
            updatedCategories.push(categoryName);
        }

        const result = await graphApiCall<any>(`/me/messages/${emailId.replace(/[\\/]/g, "-")}`, {
            method: 'PATCH',
            body: JSON.stringify({
                categories: updatedCategories
            }),
        });

        return true;
    } catch (error) {
        if (error instanceof Error && error.message.includes("Graph API call failed: 404")) {
            console.warn(`Email with ID ${emailId} not found when trying to set category. It may have been deleted.`);
            // Treat as permanent: the message no longer exists, so retrying is pointless.
            return true;
        }
        console.error("Error in setEmailCategory:", error);
        // Transient/unknown failure: signal so the caller can retry.
        return false;
    }
}